import type { Theme } from '@mui/material';
import { Box } from '@mui/material';
import type { ComponentProps } from 'react';
import { cardVariants } from '@/pages/ValuationsV2/theme';
import { combineSx } from '@/utils/combineSx';

const styles = {
  root: (theme: Theme) => ({
    position: 'relative',
    height: 'calc(100dvh - 2rem)',
    overflow: 'hidden',
    background: theme.palette.primaryBackground.backgroundColor,
    '--gap': '0.5rem',
  }),

  layout: {
    position: 'relative',
    height: '100%',
    padding: 'var(--gap)',
    display: 'flex',
    gap: 'calc(var(--gap) * 1)',
  },

  mainContent: {
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    gap: 'calc(var(--gap) * 2)',
    height: '100%',
    overflow: 'hidden',
    flex: 1,
    minWidth: 0,
  },

  previewsAndPageWrapper: {
    display: 'grid',
    gap: 'var(--gap)',
    maxHeight: '100%',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    gridTemplateAreas: {
      xs: `"pagewrapper"
           "previews"
           "actions"`,
      lg: `"previews pagewrapper"
           "previews actions"`,
    },

    gridTemplateColumns: {
      xs: '1fr',
      lg: 'clamp(12rem, 18%, 18rem) 1fr',
    },

    gridTemplateRows: {
      xs: '1fr auto auto',
      lg: '1f',
    },
    placeItems: 'start',
  },

  components: [
    { overflow: 'auto', height: '100%' },
    cardVariants({ interactive: false, padding: 'none', border: true }),
  ],

  previews: [
    {
      gridArea: 'previews',
      overflow: 'hidden',
      height: { lg: '100%' },
      width: '100%',
      '--pageNumberOpacity': 0,
      '&:hover': {
        '--pageNumberOpacity': 1,
      },
    },
    cardVariants({ interactive: false, padding: 'none', border: true }),
  ],
  pageWrapper: {
    gridArea: 'pagewrapper',
    width: 'clamp(20rem, 100%, 160rem)',
    height: '100%',
    margin: {
      lg: '0 auto',
    },
  },
  previewsScrollContainer: {
    padding: 'var(--gap)',
    height: { lg: '100%' },
    display: 'grid',
    gap: 'var(--gap)',
    placeItems: 'start',
    placeContent: 'start',
    gridTemplateColumns: {
      xs: 'repeat(auto-fill, minmax(12rem, 1fr))',
      lg: '100%',
    },
    gridTemplateRows: {
      xs: '100%',
      lg: 'max-content',
    },
    gridAutoFlow: {
      xs: 'column',
      lg: 'row',
    },
    gridAutoRows: {
      xs: 'unset',
      lg: 'max-content',
    },
    gridAutoColumns: {
      xs: 'minmax(12rem, 1fr)',
      lg: 'unset',
    },
    scrollSnapType: {
      xs: 'x proximity',
      lg: 'y proximity',
    },
    scrollBehavior: 'smooth',
    scrollPadding: 'var(--gap)',
    overflowX: {
      xs: 'auto',
      lg: 'hidden',
    },
    overflowY: {
      xs: 'hidden',
      lg: 'auto',
    },
  },
  actionsWrapper: [
    {
      display: 'grid',
      gridArea: 'actions',
      width: '100%',
      gridTemplateColumns: '1fr',
      gridAutoFlow: 'column',
      gridAutoColumns: 'max-content',
      gap: 'var(--gap)',
      padding: 'var(--gap)',
      alignItems: 'center',
    },
    cardVariants({ interactive: false, padding: 'none', border: true }),
  ],

  paginationWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    maxWidth: '8rem',
    placeItems: 'center',
    color: (theme: Theme) => theme.palette.text.primary,

    '& > span': {
      fontSize: '0.875rem',
      opacity: '0.75',
      fontVariantNumeric: 'tabular-nums',
      lineHeight: 1.3,
    },
  },
};

type LayoutProps = ComponentProps<typeof Box>;

const PreviewsWrapper = ({ children }: LayoutProps) => {
  return (
    <Box component='aside' sx={combineSx(styles.previews)} data-id='previews'>
      <Box sx={styles.previewsScrollContainer} data-id='previews-scroll-container'>
        {children}
      </Box>
    </Box>
  );
};

const PreviewsAndPageWrapper = ({ children, sx }: LayoutProps) => {
  return (
    <Box
      component='main'
      sx={combineSx(styles.previewsAndPageWrapper, sx)}
      data-id='previews-and-page-wrapper'
    >
      {children}
    </Box>
  );
};

const ComponentsWrapper = ({ children, sx }: LayoutProps) => {
  return (
    <Box component='aside' sx={combineSx(styles.components, sx)} data-id='components-wrapper'>
      {children}
    </Box>
  );
};

const Layout = ({ children, sx, ...rest }: LayoutProps) => {
  return (
    <Box component='section' {...rest} sx={combineSx(styles.layout, sx)} data-id='layout'>
      {children}
    </Box>
  );
};

const EditContent = ({ children, sx, ...rest }: LayoutProps) => {
  return (
    <Box {...rest} sx={styles.mainContent} data-id='edit-content'>
      {children}
    </Box>
  );
};

const RootWrapper = ({ children, sx, ...rest }: LayoutProps) => {
  return (
    <Box {...rest} sx={combineSx(styles.root, sx)} data-id='root-wrapper'>
      {children}
    </Box>
  );
};

const PageWrapper = ({ children, sx, ...rest }: LayoutProps) => {
  return (
    <Box {...rest} sx={combineSx(styles.pageWrapper, sx)} data-id='page-wrapper'>
      {children}
    </Box>
  );
};

const PaginationWrapper = ({ children, sx, ...rest }: LayoutProps) => {
  return (
    <Box {...rest} sx={combineSx(styles.paginationWrapper, sx)} data-id='pagination-wrapper'>
      {children}
    </Box>
  );
};

const ActionsWrapper = ({ children, sx, ...rest }: LayoutProps) => {
  return (
    <Box {...rest} sx={combineSx(styles.actionsWrapper, sx)} data-id='actions-wrapper'>
      {children}
    </Box>
  );
};

export {
  Layout,
  PageWrapper,
  EditContent,
  RootWrapper,
  PreviewsWrapper,
  PreviewsAndPageWrapper,
  ComponentsWrapper,
  PaginationWrapper,
  ActionsWrapper,
};
